// 用語切替（「センサー」→「モニタリングポイント」、「設置場所」→「管理施設」）の集約辞書と判定関数
// libs/insectTypes.js パターンに準拠（Vue 非依存、featureIsActive を引数で受け取る）
//
// 設計メモ:
// 本辞書のキーは i18n キー（例: `field.sensor_name`）ではなく、**翻訳後の表示文字列**
// （例: `'センサー名'`）である。call-site では既に `{{ $t('field.sensor_name') }}` 等で
// 翻訳済み文字列が出力されているため、`{{ $mpt($t('field.sensor_name')) }}` のように
// 翻訳結果を後段で再変換する形になる。
//
// この設計に至った理由:
// - vue-i18n v7 の `<i18n>` ブロックはコンポーネントごとに独立した子インスタンスを生成し、
//   グローバル `mergeLocaleMessage` で外部から上書きできない
// - 既存 i18n キーを触らず、トグル分岐を一箇所に集約するため、翻訳後文字列をキーにした
//
// 副作用:
// - 表記揺れに弱い（前後空白や記号差で別エントリ扱い）
// - ja / en で辞書を二重に持つ必要がある
// - 既存 i18n キーとの対応関係はコード上で明示されない

const FEATURE_KEY = 'monitoring_point';

const OVERRIDES = {
  ja: {
    // 基本用語
    センサー: 'モニタリングポイント',
    センサー名: 'モニタリングポイント名',
    識別子: 'センサー識別子',
    センサー一覧: 'モニタリングポイント一覧',
    センサー登録: 'モニタリングポイント登録',
    センサー追加: 'モニタリングポイント追加',
    センサー削除: 'モニタリングポイント削除',
    センサー数: 'モニタリングポイント数',
    センサーランキング: 'モニタリングポイントランキング',
    SENSOR: 'MONITORING POINT',

    // フレーズ
    センサーを追加: 'モニタリングポイントを追加',
    センサーを削除: 'モニタリングポイントを削除',
    センサーを共有: 'モニタリングポイントを共有',
    センサーを共有する: 'モニタリングポイントを共有する',
    センサーを作成しました: 'モニタリングポイントを作成しました',
    センサーを更新しました: 'モニタリングポイントを更新しました',
    センサー名で絞り込み: 'モニタリングポイント名で絞り込み',
    よく見るセンサー: 'よく見るモニタリングポイント',
    最寄りのセンサー: '最寄りのモニタリングポイント',
    不明なセンサー: '不明なモニタリングポイント',
    他の施設で利用中のセンサー: '他の施設で利用中のモニタリングポイント',

    // メッセージ・エラー
    センサーが登録されていません: 'モニタリングポイントが登録されていません',
    R型センサーが登録されていません: 'モニタリングポイントが登録されていません',
    一致するセンサーがありません: '一致するモニタリングポイントがありません',
    該当するセンサーがありません: '該当するモニタリングポイントがありません',
    指定されたセンサーが存在しません: '指定されたモニタリングポイントが存在しません',
    無効なセンサーが含まれています: '無効なモニタリングポイントが含まれています',
    このセンサーを削除します: 'このモニタリングポイントを削除します',
    このセンサーは他のユーザーと共有されているため削除できません:
      'このモニタリングポイントは他のユーザーと共有されているため削除できません',
    センサーを図面にドラッグして配置できます:
      'モニタリングポイントを図面にドラッグして配置できます',
    センサーを追加するとタイムラインを表示できます:
      'モニタリングポイントを追加するとタイムラインを表示できます',
    この設置場所にセンサーが登録されていません:
      'この管理施設にモニタリングポイントが登録されていません',
    この設置場所にR型センサーが登録されていません:
      'この管理施設にモニタリングポイントが登録されていません',
    すべてのセンサーを展開: 'すべてのモニタリングポイントを展開',
    センサー種別: 'モニタリングポイント種別',
    表示するセンサーがありません: '表示するモニタリングポイントがありません',
    センサーを展開: 'モニタリングポイントを展開',
    '現場の図面をアップロードして、センサー配置を管理しましょう':
      '現場の図面をアップロードして、モニタリングポイント配置を管理しましょう',
    'このセンサーのメールアドレスを再生成します。よろしいですか？':
      'このモニタリングポイントのメールアドレスを再生成します。よろしいですか？',
    'このアカウントにはセンサーを共有できません。':
      'このアカウントにはモニタリングポイントを共有できません。',
  },
  en: {
    // Basic terms
    Sensor: 'Monitoring Point',
    Sensors: 'Monitoring Points',
    'Sensor name': 'Monitoring Point name',
    Identifier: 'Sensor identifier',
    'Sensor list': 'Monitoring Point list',
    'Sensor List': 'Monitoring Point List',
    'New sensor': 'New Monitoring Point',
    'Add sensor': 'Add Monitoring Point',
    'Delete sensor': 'Delete Monitoring Point',
    'Sensor count': 'Monitoring Point count',
    'Sensor ranking': 'Monitoring Point ranking',
    'Sensor type': 'Monitoring Point type',
    'Share sensor': 'Share Monitoring Point',

    // Phrases
    'Bookmarked sensors': 'Bookmarked Monitoring Points',
    'Nearest sensor': 'Nearest Monitoring Point',
    'Unknown sensor': 'Unknown Monitoring Point',
    'Filter by sensor name': 'Filter by Monitoring Point name',
    'Sensors used by other facilities': 'Monitoring Points used by other facilities',

    // Messages
    'No sensors registered': 'No Monitoring Points registered',
    'No R-type sensors registered': 'No Monitoring Points registered',
    'No matching sensors': 'No matching Monitoring Points',
    'No sensors are assigned to this facility':
      'No Monitoring Points are assigned to this facility',
    'Add sensors to view the timeline': 'Add Monitoring Points to view the timeline',
    'This sensor is not shared with any users':
      'This Monitoring Point is not shared with any users',
    'Nearest Sensors': 'Nearest Monitoring Points',
    'View detection trends of multiple sensors by time period':
      'View detection trends of multiple Monitoring Points by time period',
    'Sensor was successfully created': 'Monitoring Point was successfully created',
    'Sensor was successfully updated': 'Monitoring Point was successfully updated',
    'An error occurred while creating the sensor':
      'An error occurred while creating the Monitoring Point',
    'An error occurred while updating the sensor':
      'An error occurred while updating the Monitoring Point',
    'The specified sensor does not exist': 'The specified Monitoring Point does not exist',
    'Invalid sensor included': 'Invalid Monitoring Point included',
    'This sensor will be deleted': 'This Monitoring Point will be deleted',
    'This sensor cannot be deleted because it is shared with other users':
      'This Monitoring Point cannot be deleted because it is shared with other users',
    'Drag sensor to floor plan to place': 'Drag Monitoring Point to floor plan to place',
    'Add sensors to display timeline': 'Add Monitoring Points to display timeline',
    'No sensors registered at this installation site':
      'No Monitoring Points registered at this management facility',
    'No R-type sensors assigned to this place':
      'No Monitoring Points registered at this management facility',
    'There are no sensors to display': 'There are no Monitoring Points to display',
    'Upload a floor plan to manage sensor placement':
      'Upload a floor plan to manage Monitoring Point placement',
    'The mail address of this sensor will be newly generated. Are you sure?':
      'The mail address of this Monitoring Point will be newly generated. Are you sure?',
    'This sensor cannot be shared with this account.':
      'This Monitoring Point cannot be shared with this account.',
  },
};

/**
 * テキストを「モニタリングポイント」表記に切り替える。
 * featureIsActive('monitoring_point') が false の場合、引数をそのまま返す。
 *
 * @param {string} text 元のテキスト
 * @param {string} locale i18n locale ('ja' | 'en')
 * @param {function} featureIsActive (feature: string) => boolean
 * @returns {string}
 */
export const mpt = (text, locale, featureIsActive) => {
  if (!featureIsActive(FEATURE_KEY)) return text;
  const dict = OVERRIDES[locale];
  if (!dict) return text;
  return dict[text] || text;
};
